/**
 * Mappe les erreurs Supabase Auth vers une clé de traduction (`auth.errors.*`).
 *
 * Le composant appelant résout la clé via `useTranslations('auth')` —
 * `toast.error(t(`errors.${mapAuthError(err) ?? 'fallbackSignup'}`))` par exemple.
 *
 * Retourne `null` quand aucune correspondance n'est trouvée pour que l'appelant
 * choisisse son propre fallback contextuel (login, signup, reset, oauth…).
 */

type SupabaseAuthErrorShape = {
  name?: string;
  code?: string;
  status?: number;
  message?: string;
};

export type AuthErrorKey =
  | 'userAlreadyExists'
  | 'identityAlreadyExists'
  | 'invalidCredentials'
  | 'emailNotConfirmed'
  | 'emailAddressInvalid'
  | 'emailAddressNotAuthorized'
  | 'weakPassword'
  | 'samePassword'
  | 'signupDisabled'
  | 'userBanned'
  | 'userNotFound'
  | 'overRequestRateLimit'
  | 'overEmailSendRateLimit'
  | 'captchaFailed'
  | 'otpExpired'
  | 'sessionExpired'
  | 'providerDisabled'
  | 'badOauthState'
  | 'requestTimeout'
  | 'network';

function asAuthError(err: unknown): SupabaseAuthErrorShape | null {
  if (typeof err !== 'object' || err === null) return null;
  const candidate = err as SupabaseAuthErrorShape;
  if (typeof candidate.message !== 'string') return null;
  return candidate;
}

const CODE_KEYS: Record<string, AuthErrorKey> = {
  user_already_exists: 'userAlreadyExists',
  email_exists: 'userAlreadyExists',
  identity_already_exists: 'identityAlreadyExists',
  invalid_credentials: 'invalidCredentials',
  email_not_confirmed: 'emailNotConfirmed',
  email_address_invalid: 'emailAddressInvalid',
  email_address_not_authorized: 'emailAddressNotAuthorized',
  weak_password: 'weakPassword',
  same_password: 'samePassword',
  signup_disabled: 'signupDisabled',
  user_banned: 'userBanned',
  user_not_found: 'userNotFound',
  over_request_rate_limit: 'overRequestRateLimit',
  over_email_send_rate_limit: 'overEmailSendRateLimit',
  captcha_failed: 'captchaFailed',
  otp_expired: 'otpExpired',
  session_expired: 'sessionExpired',
  provider_disabled: 'providerDisabled',
  bad_oauth_state: 'badOauthState',
  bad_oauth_callback: 'badOauthState',
  request_timeout: 'requestTimeout',
};

const MESSAGE_FALLBACKS: ReadonlyArray<{ test: RegExp; key: AuthErrorKey }> = [
  { test: /user already registered|already (registered|exists)/i, key: 'userAlreadyExists' },
  { test: /invalid login credentials|invalid credentials/i, key: 'invalidCredentials' },
  { test: /email not confirmed/i, key: 'emailNotConfirmed' },
  { test: /password should be at least|weak password/i, key: 'weakPassword' },
  { test: /rate limit|too many requests/i, key: 'overRequestRateLimit' },
  { test: /network|failed to fetch/i, key: 'network' },
];

export function mapAuthError(err: unknown): AuthErrorKey | null {
  const authErr = asAuthError(err);
  if (!authErr) return null;

  if (authErr.code && CODE_KEYS[authErr.code]) {
    return CODE_KEYS[authErr.code];
  }

  if (authErr.message) {
    for (const { test, key } of MESSAGE_FALLBACKS) {
      if (test.test(authErr.message)) return key;
    }
  }

  return null;
}
